import React, { useState } from 'react'
import { Button, Drawer } from '@sainsburys-tech/bolt'
import Image from '../../../helpers/Image/Image'
import { eventNames, trackDataLayerEvent, createGolContentDataAttributes } from '../../../helpers/tracking'
import { C009Props } from './types'
import {
  StyledSmallProposition,
  StyledCopy,
  StyledLink,
  StyledContentWrapper,
  StyledProposition,
  StyledRichTextWrapper,
  StyledInView
} from './C009.styles'

const C009 = ({
  backgroundColor,
  copy,
  ctaCopy,
  consumingGridType = 'isolated',
  positionInGrid,
  gridItems,
  image = {
    url: '',
    altText: ''
  },
  imageFormat,
  isSmall = false,
  link,
  termsAndConditions,
  textTheme = 'dark',
  valid = true
}: C009Props): React.ReactElement => {
  if (!valid) return null

  const [showTermsDrawer, toggleTermsDrawer] = useState(false)
  const [isViewed, setViewed] = useState(false)

  const copyMarkup = () => ({ __html: copy.replace(/h6/g, 'small') })
  const termsMarkup = () => ({ __html: termsAndConditions?.text })

  // gridItems will be undefined for isolated component type
  const showMargin = !!(gridItems === 1 || consumingGridType === 'isolated')

  const getGOLDataAttributes = (action?: string) =>
    createGolContentDataAttributes(
      consumingGridType,
      `C009 - ${positionInGrid !== undefined ? `Position${positionInGrid}` : 'NA'}`,
      link?.title,
      link?.url,
      copy,
      action
    )

  const addGolTracking = (event: React.MouseEvent) => {
    const dataAttributes = getGOLDataAttributes()
    if (dataAttributes && Object.keys(dataAttributes).length) {
      Object.keys(dataAttributes).forEach((attribute) =>
        event.currentTarget.setAttribute(attribute, dataAttributes[attribute])
      )
    }
  }

  const onComponentViewed = () => {
    setViewed(true)
    trackDataLayerEvent(eventNames.afsBannerInView)
  }

  const onInteraction = (e: React.MouseEvent, interaction: string) => {
    if (termsAndConditions) {
      toggleTermsDrawer(!showTermsDrawer)
    }
    trackDataLayerEvent(interaction)
    if (link?.url && interaction === eventNames.afsBannerClicked) {
      addGolTracking(e)
    }
  }

  const renderDrawer = () => {
    const footer = (
      <Button
        onClick={(e) => onInteraction(e, eventNames.afsDrawerCloseKeepOnShopping)}
        full
        data-test='c009-drawer-button'
      >
        Keep On Shopping
      </Button>
    )
    const drawerProps = {
      heading: termsAndConditions.title,
      position: 'right',
      show: showTermsDrawer,
      onClose: (e) => onInteraction(e, eventNames.afsDrawerCloseX),
      footer
    }
    return (
      <Drawer {...drawerProps}>
        <span dangerouslySetInnerHTML={termsMarkup()} />
      </Drawer>
    )
  }

  const renderButton = () => (
    <Button
      as={link?.url ? 'a' : undefined}
      href={link?.url || undefined}
      title={link?.title || undefined}
      target={link?.target || undefined}
      onClick={(e) => onInteraction(e, eventNames.afsBannerClicked)}
      kind='secondary'
    >
      {ctaCopy}
    </Button>
  )

  if (isSmall) {
    return (
      <StyledInView
        onChange={(inView) => inView && !isViewed && onComponentViewed()}
        showMargin={showMargin}
        {...getGOLDataAttributes('Load')}
        data-test='c009-inview-wrapper'
      >
        <StyledRichTextWrapper>
          <StyledSmallProposition
            as={link?.url ? 'a' : undefined}
            href={link?.url || undefined}
            title={link?.title || undefined}
            target={link?.target || undefined}
            backgroundColor={backgroundColor}
            textTheme={textTheme}
            onClick={(e) => onInteraction(e, eventNames.afsBannerClicked)}
            data-test='c009-container-small'
          >
            <div data-test='c009-proposition-content-container'>
              <Image imageFormat={imageFormat} data-test='c009-proposition-image' src={image.url} alt={image.altText} />
              <StyledContentWrapper>
                <StyledCopy
                  data-test='c009-proposition-copy'
                  dangerouslySetInnerHTML={copyMarkup()}
                  isSmall={isSmall}
                />
                <StyledLink data-test='c009-proposition-ctaCopy' textTheme={textTheme}>
                  {ctaCopy}
                </StyledLink>
              </StyledContentWrapper>
            </div>
          </StyledSmallProposition>
          {showTermsDrawer && renderDrawer()}
        </StyledRichTextWrapper>
      </StyledInView>
    )
  }

  return (
    <StyledInView
      onChange={(inView) => inView && !isViewed && onComponentViewed()}
      showMargin={showMargin}
      {...getGOLDataAttributes('Load')}
      data-test='c009-inview-wrapper'
    >
      <StyledRichTextWrapper>
        <StyledProposition backgroundColor={backgroundColor} textTheme={textTheme} data-test='c009-container'>
          <div>
            <StyledCopy data-test='c009-proposition-copy' dangerouslySetInnerHTML={copyMarkup()} isSmall={isSmall} />
            {renderButton()}
          </div>
          <Image imageFormat={imageFormat} data-test='c009-proposition-image' src={image.url} alt={image.altText} />
        </StyledProposition>
        {showTermsDrawer && renderDrawer()}
      </StyledRichTextWrapper>
    </StyledInView>
  )
}

export default C009
